/**
 * CR-8675: how an import job's failure reads in Recent Failures and Health
 * Check.
 *
 * A job that fails before reading anything (an unreadable file, no data source
 * configured) has no record counts, so "0 failed of 0" made a genuine failure
 * look like nothing had gone wrong. Say what happened instead.
 */
import type { ImportJob, ImportSource } from '../../lib/api/imports';

export const NO_RECORDS_READ = 'Failed before any records were read';
export const NO_REASON_RECORDED = 'No reason recorded';

/** True when the job never got as far as reading records. */
export function failedBeforeReading(job: Pick<ImportJob, 'total_records'>): boolean {
  return !job.total_records;
}

/** "3 failed of 120", or NO_RECORDS_READ when nothing was read. */
export function failedCountText(job: Pick<ImportJob, 'total_records' | 'records_failed'>): string {
  if (failedBeforeReading(job)) return NO_RECORDS_READ;
  return `${job.records_failed.toLocaleString()} failed of ${job.total_records.toLocaleString()}`;
}

export const LOGS_NOT_KEPT =
  'Record logs for this job are no longer kept. Logs are kept for 7 days, '
  + 'and always for the latest two imports of each type.';
export const NO_RECORDS_IN_JOB = 'No records were read in this job.';
export const NO_LOGS_FOR_FILTER = 'No logs for this filter';
export const MANUAL_IMPORT_LOGS_FAILURES_ONLY =
  'No rows failed. Manual data imports record a log only for rows that failed.';

/**
 * What an empty Record Logs table means. Under "All", a live-feed job that
 * processed records but has no logs has aged out of retention (backend
 * import_log_retention) — "No logs for this filter" read as if the import
 * had recorded nothing. A manual (spreadsheet) import never logs rows that
 * imported cleanly, so for it an empty table means nothing failed.
 */
export function emptyLogsText(filter: string, recordsProcessed: number, source: ImportSource): string {
  if (filter !== 'all') return NO_LOGS_FOR_FILTER;
  if (recordsProcessed === 0) return NO_RECORDS_IN_JOB;
  return source === 'data_import' ? MANUAL_IMPORT_LOGS_FAILURES_ONLY : LOGS_NOT_KEPT;
}
